import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useReducer, useRef, useState } from 'react'

import {
  conversationIdAtom,
  deepseekMethodAtom,
  IMessage,
  chatMessagesAtom,
  IConversation,
  selectMessageAtom,
  placeholderAtom,
  isUpdateHistoryAtom,
} from '~/components/pages/deepseek/atom'
import dayjs from 'dayjs'

import { createParser } from 'eventsource-parser'
import { getDeepseekMessage } from '~/apis'
import { iterableStreamAsync } from '~/utils'
import { useMutation } from '@apollo/client'
import { INIT_CONVERSATION } from '~/graphqls/gqls/gpt'
import {
  conversationInitiate,
  conversationInitiateVariables,
} from '~/graphqls/types'
import { message } from 'antd'
import { ChatRole } from '~/graphqls/customType'

export const updateMessages = ({
  chatMessages,
  conversationId,
  messageIndex,
  newMessage,
  action,
}: {
  chatMessages: Record<any, IConversation>
  conversationId: any
  messageIndex: number
  newMessage: Partial<IMessage>
  action: 'add' | 'update'
}) => {
  const updatedConversations = { ...chatMessages }
  const conversation = updatedConversations[conversationId]
  if (!conversation) {
    return {
      ...updatedConversations,
      [conversationId || 'default']: {
        messages: [newMessage],
      },
    }
  }
  const updatedMessages = [...(conversation.messages || [])]

  if (action === 'update') {
    // 更新操作
    if (messageIndex < 0 || messageIndex >= updatedMessages.length) {
      console.error('Invalid message index')
      return updatedConversations
    }
    updatedMessages[messageIndex] = {
      ...updatedMessages[messageIndex],
      ...newMessage,
    }
  } else {
    // 新增操作
    updatedMessages.push(newMessage as IMessage)
  }

  const updatedConversation = { ...conversation, messages: updatedMessages }
  updatedConversations[conversationId] = updatedConversation
  return updatedConversations
}

export const useDeepSeekMotion = () => {
  const setMessages = useSetAtom(chatMessagesAtom)
  const selectMessage = useAtomValue(selectMessageAtom)
  const [selectedMethods] = useAtom(deepseekMethodAtom)
  const [isTyping, setIsTyping] = useState<Record<number, boolean>>()
  const isTypingRef = useRef<boolean>(false)
  const [isThinking, setIsThinking] = useState<Record<number, boolean>>()
  const [conversationId, setConversationId] = useAtom(conversationIdAtom)
  const setPlaceholder = useSetAtom(placeholderAtom)
  const setIsUpdateHistory = useSetAtom(isUpdateHistoryAtom)

  const signalRef = useRef<Record<number, AbortController>>()
  const [, forceUpdate] = useReducer(() => ({}), {})

  const [initAction] = useMutation<
    conversationInitiate,
    conversationInitiateVariables
  >(INIT_CONVERSATION)
  const handleSSE = async (
    response: Response,
    onMessage: (message: string) => void
  ) => {
    const parser = createParser(event => {
      if (event.type === 'event') {
        onMessage(event.data)
      }
    })
    for await (const chunk of iterableStreamAsync(response.body!)) {
      const str = new TextDecoder().decode(chunk)
      parser.feed(str)
    }
  }

  const renderError = (errorReason: string) => {
    setMessages(prev => {
      return updateMessages({
        chatMessages: prev,
        conversationId: conversationId,
        messageIndex: 0,
        action: 'add',
        newMessage: {
          id: dayjs().valueOf().toString(),
          role: ChatRole.ASSISTANT,
          content: '',
          error: true,
          errorReason,
          parentId: 0,
        },
      }) as Record<any, IConversation>
    })
    forceUpdate()
  }

  const sendMessage = async ({ input }: { input: string }) => {
    if (!input.trim()) return
    const userMessage = {
      role: ChatRole.USER,
      content: input,
      id: dayjs().valueOf().toString(),
      parentId: 0,
    }
    let id = conversationId
    setMessages(prev => {
      // 没有创建会话，使用default
      if (!id) {
        return updateMessages({
          chatMessages: prev,
          conversationId: 'default',
          messageIndex: 0,
          action: 'add',
          newMessage: userMessage,
        }) as Record<any, IConversation>
      } else {
        return updateMessages({
          chatMessages: prev,
          conversationId: id,
          messageIndex: 0,
          action: 'add',
          newMessage: userMessage,
        }) as Record<any, IConversation>
      }
    })

    setPlaceholder({
      id: '_optimistic',
      role: ChatRole.ASSISTANT,
      content: '',
      reasoning: '',
      thinkingTime: 0,
      searchEnabled: selectedMethods.includes('search'),
      thinkingEnabled: selectedMethods.includes('thinking'),
      model: selectedMethods.includes('thinking')
        ? 'DEEP_SEEK_R1'
        : 'DEEP_SEEK_V3',
    })

    try {
      const searchEnabled = selectedMethods.includes('search')
      const thinkingEnabled = selectedMethods.includes('thinking')
      if (!id) {
        const initRes = await initAction({
          variables: {
            input: {
              title: input.length > 30 ? input.slice(0, 30) + '...' : input,
            },
          },
        })
        setConversationId(
          initRes?.data?.openAiMutation?.conversationInitiate?.id
        )
        id = initRes?.data?.openAiMutation?.conversationInitiate?.id

        setMessages(prev => {
          return updateMessages({
            chatMessages: prev,
            conversationId: id,
            messageIndex: 0,
            action: 'add',
            newMessage: {
              ...prev?.default?.messages?.[0],
              conversationId: id!,
              parentId: 0,
            },
          }) as Record<any, IConversation>
        })
        setIsUpdateHistory(true)
      } else {
        setIsUpdateHistory(false)
      }

      setIsTyping(prev => {
        return {
          ...prev,
          [id!]: true,
        }
      })
      isTypingRef.current = true

      setIsThinking(prev => {
        return {
          ...prev,
          [id!]: false,
        }
      })

      signalRef.current = {
        ...signalRef.current,
        [id!]: new AbortController(),
      }

      const assistantMessage = selectMessage?.filter(
        it => it.role === 'assistant' && !!it.parentId
      )

      const response = await getDeepseekMessage(
        {
          conversationId: id,
          parentId: !!assistantMessage?.length
            ? assistantMessage[assistantMessage?.length - 1].parentId
            : 0,
          prompt: input,
          historicalChatMessage: selectMessage.map(it => ({
            role: it.role,
            content: it.content || '',
          })),
          searchEnabled: searchEnabled,
          thinkingEnabled: thinkingEnabled,
          model: thinkingEnabled ? 'DEEP_SEEK_R1' : 'DEEP_SEEK_V3',
        },
        {
          signal: signalRef.current?.[id!]?.signal,
        }
      )

      if (response.status === 417) {
        message.destroy()
        message.error('此功能仅限VIP会员用户使用')

        return
      }

      let messageContent = ''
      let reasoningContent = ''
      // 思考开始时间
      let startTime = 0
      // 思考结束时间
      let endTime = 0
      let innerThinking = false

      await handleSSE(response, message => {
        if (message === '[DONE]') {
          setMessages(prev => {
            return updateMessages({
              chatMessages: prev,
              conversationId: id,
              messageIndex: (prev?.[id!]?.messages?.length ?? 0) - 1,
              action: 'update',
              newMessage: {
                done: true,
              },
            }) as Record<any, IConversation>
          })

          return
        }

        if (message.startsWith('Error')) {
          renderError(message)
          return
        }
        const data = JSON.parse(message)
        if (data.error) {
          renderError(data.error)
          return
        }
        const content = data?.choices?.[0]?.delta?.content || ''

        if (content.startsWith('<think>')) {
          setIsThinking(prev => {
            return {
              ...prev,
              [data?.chunk_meta?.conversation_id!]: true,
            }
          })
          innerThinking = true
          reasoningContent += content.replace('<think>', '')
          startTime = dayjs().unix()
        } else if (content.startsWith('</think>')) {
          setIsThinking(prev => {
            return {
              ...prev,
              [data?.chunk_meta?.conversation_id!]: false,
            }
          })
          innerThinking = false
          reasoningContent += content.replace('</think>', '')
          endTime = dayjs().unix()
        } else if (innerThinking) {
          reasoningContent += content
        } else {
          messageContent += content
        }
        if (!!data?.chunk_meta?.msg_id) {
          if (thinkingEnabled && reasoningContent) {
            setPlaceholder(null)
          }
          if (
            !thinkingEnabled &&
            messageContent.replace(/<think>.*?<\/think>/g, '')
          ) {
            setPlaceholder(null)
          }
          setMessages(prev => {
            const assistantMessage: any = {
              id: data?.chunk_meta?.msg_id,
              parentId: data?.chunk_meta?.msg_id,
              conversationId: data?.chunk_meta?.conversation_id,
              role: ChatRole.ASSISTANT,
              content: messageContent.replace(/<think>.*?<\/think>/g, ''),
              reasoning: reasoningContent,
              thinkingTime: !innerThinking ? endTime - startTime : 0,
              searchEnabled: data?.chunk_meta?.search_enabled ?? false,
              thinkingEnabled: data?.chunk_meta?.thinking_enabled ?? false,
            }
            if (data?.choices?.[0]?.delta?.search_results) {
              assistantMessage.searchResults =
                data?.choices?.[0]?.delta?.search_results
            }
            if (
              !!prev?.[data?.chunk_meta?.conversation_id]?.messages?.filter(
                it =>
                  it?.id === assistantMessage?.id &&
                  it.role === ChatRole.ASSISTANT
              )?.length
            ) {
              return updateMessages({
                chatMessages: prev,
                conversationId: assistantMessage.conversationId,
                messageIndex: prev?.[
                  assistantMessage.conversationId
                ]?.messages?.findIndex(it => it?.id === assistantMessage?.id)!,
                action: 'update',
                newMessage: assistantMessage,
              }) as Record<any, IConversation>
            } else {
              return updateMessages({
                chatMessages: prev,
                conversationId: assistantMessage.conversationId,
                messageIndex: 0,
                action: 'add',
                newMessage: assistantMessage,
              }) as Record<any, IConversation>
            }
          })
          forceUpdate()
        }
      })
    } catch (error) {
      // 错误处理
      console.error('Error streaming response:', error)
    } finally {
      setIsTyping(prev => {
        return {
          ...prev,
          [id!]: false,
        }
      })
      isTypingRef.current = false
      setPlaceholder(null)
    }
  }

  const stopMessage = async () => {
    if (!conversationId) return
    try {
      signalRef.current?.[conversationId!]?.abort()
    } catch (e) {
      console.log(e, 'stopMessage')
    }
    setMessages(prev => {
      return updateMessages({
        chatMessages: prev,
        conversationId: conversationId,
        messageIndex: (prev?.[conversationId!]?.messages?.length ?? 0) - 1,
        action: 'update',
        newMessage: {
          done: true,
        },
      }) as Record<any, IConversation>
    })
    setIsThinking(prev => {
      return {
        ...prev,
        [conversationId!]: false,
      }
    })
    setIsTyping(prev => {
      return {
        ...prev,
        [conversationId!]: false,
      }
    })
    isTypingRef.current = false
  }

  return {
    /** 消息列表 */
    messages: selectMessage,
    /** 发送消息函数 */
    sendMessage,
    /** 机器人是否正在输入 */
    isTyping,
    /** 机器人是否正在输入： ref */
    isTypingRef,
    /** 输出think内容中 */
    isThinking,
    stopMessage,
  }
}
